import {
	LoadingOutlined,
	CheckOutlined,
	DownOutlined,
	ExclamationCircleOutlined,
} from '@ant-design/icons';
import * as React from 'react';
import { cn } from '../../../utils/ClassNamesUtils';
import { AgentTranscriptEntry } from '../../../types/agentHarness';
import { humanizeToolName } from '../../utils/OscarLabels';
import { OSCAR_RETRY_COPY } from '../../constants/AIAgentConstants';
import OscarThinkingIndicator, { formatElapsed } from '../shared/OscarThinkingIndicator';

type ToolEntry = Extract<AgentTranscriptEntry, { kind: 'tool' }>;

/**
 * One thing the agent did on the way to its answer: a tool call, a stretch of
 * reasoning, or something it said between tool calls ("Let me look that up").
 *
 * When the harness retried the turn on a stronger model, what the abandoned
 * attempt wrote is a `discarded` step — kept for the record, never the answer —
 * and `restart` marks where the retry began.
 */
export type OscarActivityStep =
	| { kind: 'tool'; id: string; tool: ToolEntry }
	| { kind: 'reasoning'; id: string; text: string }
	| { kind: 'note'; id: string; text: string }
	| { kind: 'discarded'; id: string; text: string }
	| { kind: 'restart'; id: string };

/** Set while this is the turn's in-flight row; `useOscarTurnStatus` feeds it. */
export interface OscarActivityLiveStatus {
	label: string;
	seconds: number;
	awaitingApproval: boolean;
}

interface OscarActivityTraceProps {
	/** Everything the agent did between two things the reader needs, in order. */
	steps: OscarActivityStep[];
	/**
	 * Present while the turn is running: the row is then the turn's progress
	 * indicator rather than its summary. Empty `steps` with `live` set is the
	 * moment between sending and the first stream event.
	 */
	live?: OscarActivityLiveStatus | null;
	/** How long the turn took, when it was watched live. Unknown for history. */
	durationSeconds?: number;
	/**
	 * Tool calls across the whole turn, for the row carrying its summary. A long
	 * remark can split a turn's work over two rows, and the turn's time beside
	 * only the last row's count would understate the work. Defaults to this
	 * row's own count.
	 */
	stepCount?: number;
	/** The turn ended on a failed tool call with no answer after it. */
	failed?: boolean;
	/** The harness set a first pass aside and retried the turn. */
	retried?: boolean;
	className?: string;
}

/**
 * Turns everything the agent did before answering into one collapsible line.
 *
 * A single question can interleave reasoning, narration and several rounds of
 * tool calls. Rendered as a line each — even with each run of tools collapsed —
 * that work pushed the answer off screen, so it all folds into one row and the
 * answer is the only thing left in the conversation by default.
 *
 * While the turn runs, the same row is the progress indicator — Oscar, the
 * elapsed time and what is happening — so there is one row for the turn's work
 * from the moment the message is sent, and it settles into the summary in place.
 *
 * Tool payloads are deliberately not shown at all: a row states what ran and
 * how it came back, and nothing more.
 */
/**
 * Anything that looks like a serialized payload rather than a sentence. The
 * harness sends a *truncated* preview as `summary`, so a large result arrives as
 * JSON that no longer parses — which is how `{ "items": [ { "id": "273768…`
 * ended up on screen as a tool's outcome.
 */
const looksLikePayload = (text: string): boolean => {
	const trimmed = text.trim();
	return (
		trimmed.charAt(0) === '{' ||
		trimmed.charAt(0) === '[' ||
		trimmed.indexOf('":') !== -1 ||
		trimmed.indexOf('\\n') !== -1
	);
};

const outcomeFor = (tool: ToolEntry): string => {
	if (!tool.done) {
		return 'running…';
	}
	if (tool.isError) {
		return 'failed';
	}
	if (!tool.summary) {
		return 'done';
	}
	try {
		const parsed = JSON.parse(tool.summary);
		if (parsed && Array.isArray(parsed.items)) {
			const total = typeof parsed.total === 'number' ? parsed.total : parsed.items.length;
			return `${total} result${total === 1 ? '' : 's'}`;
		}
		if (parsed && typeof parsed.stdout === 'string' && parsed.stdout.trim()) {
			return parsed.stdout.trim().split('\n')[0];
		}
		if (parsed && parsed.ok === true) {
			return 'ok';
		}
	} catch (parseError) {
		// Not parseable — usually a payload the harness already truncated.
	}
	// Never fall back to the payload itself. A row says what ran and how it came
	// back; if the result cannot be described in a few words, "done" is the
	// honest version and the answer below is where the content belongs.
	if (looksLikePayload(tool.summary)) {
		return 'done';
	}
	return tool.summary.length > 40 ? `${tool.summary.slice(0, 40)}…` : tool.summary;
};

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * The settled summary. Lower case: it is ambient status, like the thinking
 * indicator's label.
 *
 * A failed tool call on its own does not colour the summary — the agent often
 * retries or works around it, and painting a turn that ended in a good answer
 * red says the opposite of what happened. `failed` means the turn actually
 * ended on the error; the failed row inside is marked either way.
 */
const summaryFor = (
	stepCount: number,
	durationSeconds: number,
	failed: boolean,
	retried: boolean
): string => {
	const duration = durationSeconds > 0 ? formatElapsed(durationSeconds) : '';
	let summary: string;
	if (!stepCount) {
		summary = duration ? `thought for ${duration}` : 'reasoning';
	} else if (duration) {
		summary = `worked for ${duration} · ${plural(stepCount, 'step')}`;
	} else {
		summary = `worked through ${plural(stepCount, 'step')}`;
	}
	if (retried) {
		summary = `${summary} · ${OSCAR_RETRY_COPY.summary}`;
	}
	return failed ? `${summary} · failed` : summary;
};

/**
 * What the agent said it was about to do, while that is still what it is
 * doing. "Let me check the open invoices" says more than "running list
 * invoices", and it is the agent's own sentence.
 *
 * Shown while it is the latest step — still streaming, or waiting on the tool
 * call it announced — and while a tool after it is still running. Once those
 * finish, the sentence describes the past and the stream's own status takes
 * over.
 */
const liveNarration = (steps: OscarActivityStep[]): string | null => {
	let toolRunning = false;
	for (let index = steps.length - 1; index >= 0; index -= 1) {
		const step = steps[index];
		if (step.kind === 'tool' && !step.tool.done) {
			toolRunning = true;
		}
		// A note from before the retry describes the discarded attempt, not what
		// is running now.
		if (step.kind === 'restart') {
			return null;
		}
		if (step.kind === 'note') {
			return toolRunning || index === steps.length - 1 ? step.text.trim() : null;
		}
	}
	return null;
};

/**
 * Not `rotate-180`: preflight is off, so Tailwind never emits the universal
 * block that defines --tw-translate-x/skew/scale, and one unresolved var()
 * voids the whole `transform` declaration. An arbitrary value sets `transform`
 * outright and sidesteps it.
 */
const CHEVRON_CLASS_NAME =
	'shrink-0 text-[10px] transition-transform group-open:[transform:rotate(180deg)] motion-reduce:transition-none';

const ToolRow = ({ tool }: { tool: ToolEntry }) => (
	// The raw identifier stays on the title: when the agent does something
	// unexpected, the exact tool is the first thing anyone asks for.
	<div title={tool.name} className="flex items-center gap-2xs">
		{/* Not a ✕: that reads as a dismiss control on a row nobody can
		    dismiss. An exclamation says failed and invites no click, and
		    keeps the column aligned with the ticks above it. */}
		{tool.isError ? (
			<ExclamationCircleOutlined className="shrink-0 text-[10px] text-character-danger" />
		) : tool.done ? (
			<CheckOutlined className="shrink-0 text-[10px] text-character-secondary" />
		) : (
			/* A tick beside "running…" claims an outcome the tool has not
			   reached yet, which matters most on the writes this trace
			   exists to make auditable. */
			<LoadingOutlined className="shrink-0 text-[10px] text-character-secondary" />
		)}
		<span
			// Footnote, like the outcome beside it and the summary above it:
			// everything inside a disclosure is secondary by definition.
			className={cn(
				'footnoteregular truncate',
				tool.isError ? 'text-character-danger' : 'text-grey-9'
			)}
		>
			{humanizeToolName(tool.name)}
		</span>
		<span className="footnoteregular min-w-0 flex-1 truncate text-character-secondary">
			{outcomeFor(tool)}
		</span>
	</div>
);

export const OscarActivityTrace = ({
	steps,
	live,
	durationSeconds = 0,
	stepCount,
	failed = false,
	retried = false,
	className,
}: OscarActivityTraceProps) => {
	const tools = steps
		.filter((step) => step.kind === 'tool')
		.map((step) => (step as Extract<OscarActivityStep, { kind: 'tool' }>).tool);
	// An approval outranks the narration: the tool it gates is "running", but
	// what the turn is really doing is waiting on the reader.
	const narration = live && !live.awaitingApproval ? liveNarration(steps) : null;
	const indicator = live ? (
		<OscarThinkingIndicator label={narration || live.label} seconds={live.seconds} />
	) : null;

	// Nothing to expand yet — just the indicator, so the row is already in place
	// when the first step lands in it.
	if (!steps.length) {
		return indicator ? <div className={cn('w-full min-w-0', className)}>{indicator}</div> : null;
	}

	return (
		<details className={cn('group w-full min-w-0', className)}>
			<summary
				className="flex cursor-pointer list-none items-center gap-2xs text-character-secondary
					[&::-webkit-details-marker]:hidden"
			>
				<DownOutlined className={CHEVRON_CLASS_NAME} />
				{indicator ? (
					// The indicator is full width on its own; beside the chevron it has
					// to give way, or the row overflows the narrow dock.
					<div className="min-w-0 flex-1">{indicator}</div>
				) : (
					<span className={cn('footnoteregular', failed && 'text-character-danger')}>
						{summaryFor(
							typeof stepCount === 'number' ? stepCount : tools.length,
							durationSeconds,
							failed,
							retried
						)}
					</span>
				)}
			</summary>
			{/* A rail down the left ties the turn's work together as one thing. */}
			<div className="ml-[5px] flex flex-col gap-2xs border-0 border-l border-solid border-grey-4 py-2xs pl-s">
				{steps.map((step) => {
					if (step.kind === 'tool') {
						return <ToolRow key={step.id} tool={step.tool} />;
					}
					if (step.kind === 'restart') {
						return (
							<span key={step.id} className="footnoteMedium text-grey-9">
								{OSCAR_RETRY_COPY.restart}
							</span>
						);
					}
					return (
						<span
							key={step.id}
							className={cn(
								'footnoteregular whitespace-pre-wrap break-words',
								// Narration is something Oscar said, so it reads a step darker
								// than his reasoning. A discarded first pass is struck through:
								// it is on record, but it is not what Oscar concluded.
								step.kind === 'note' ? 'text-grey-9' : 'text-character-secondary',
								step.kind === 'discarded' && 'line-through'
							)}
						>
							{step.text}
						</span>
					);
				})}
			</div>
		</details>
	);
};

export default OscarActivityTrace;
